import {useEffect, useState} from "react";
import {PhoneHQButton} from "ch3-ui-lib";
import {X} from "lucide-react";
import {AccessSelector, type AccessMode, type SelectedUser, type SelectedGroup} from "./AccessSelector";
import {useBulkUpdateContactAccessMutation} from "../../store/features/Contacts/contactsService";
import type {PrivateAvailabilityRestriction} from "./contactAccessPolicy";

interface ChangeAccessDialogProps {
    open: boolean;
    onClose: () => void;
    contactIds: number[];
    /** Called after the access change succeeds (e.g. to clear the selection). */
    onSuccess?: () => void;
    privateRestriction?: PrivateAvailabilityRestriction;
}

export default function ChangeAccessDialog({
    open,
    onClose,
    contactIds,
    onSuccess,
    privateRestriction,
}: ChangeAccessDialogProps) {
    const [availability, setAvailability] = useState<AccessMode>("PUBLIC");
    const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([]);
    const [bulkUpdateAccess, {isLoading}] = useBulkUpdateContactAccessMutation();

    // Reset the form every time the dialog opens.
    useEffect(() => {
        if (open) {
            setAvailability(privateRestriction ? "PUBLIC" : "PRIVATE");
            setSelectedUsers([]);
            setSelectedGroups([]);
        }
    }, [open, privateRestriction]);

    if (!open) return null;

    const handleConfirm = async () => {
        const shared = availability === "SHARED";
        try {
            await bulkUpdateAccess({
                contactIds,
                availability,
                sharedForUsers: shared ? selectedUsers.map(u => u.userId) : [],
                sharedForGroups: shared ? selectedGroups.map(g => g.groupId) : [],
            }).unwrap();
            onSuccess?.();
            onClose();
        } catch {
            // Failure toast is dispatched by the mutation.
        }
    };

    const confirmDisabled =
        isLoading ||
        contactIds.length === 0 ||
        (availability === "PRIVATE" && privateRestriction !== undefined) ||
        (availability === "SHARED" && selectedUsers.length === 0 && selectedGroups.length === 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-scrim" onClick={onClose}/>

            <div className="relative bg-surface rounded-[20px] w-[480px] max-w-[90vw] max-h-[90vh] flex flex-col shadow-overlay overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-line pl-5 pr-3 py-3.5">
                    <span className="text-sm font-semibold text-ink">Change access</span>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                    >
                        <X size={18} className="!p-0 text-ink-muted"/>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    <p className="text-footnote text-ink-muted mb-4">
                        Choose who can access the {contactIds.length} selected contact{contactIds.length === 1 ? "" : "s"}.
                    </p>
                    <AccessSelector
                        value={availability}
                        onChange={setAvailability}
                        selectedUsers={selectedUsers}
                        selectedGroups={selectedGroups}
                        onSelectedUsersChange={setSelectedUsers}
                        onSelectedGroupsChange={setSelectedGroups}
                        active={open}
                        disabledOptions={privateRestriction ? {PRIVATE: privateRestriction.reason} : undefined}
                    />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-line bg-canvas">
                    <PhoneHQButton variant="secondary" onClick={onClose}>Cancel</PhoneHQButton>
                    <PhoneHQButton variant="primary" onClick={handleConfirm} disabled={confirmDisabled}>
                        Change access
                    </PhoneHQButton>
                </div>
            </div>
        </div>
    );
}
